import React, { useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { DrawerProps } from "./types";

const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  direction = "left",
  size = "250px",
  overlayColor = "black",
  overlayOpacity = 0.5,
  duration = 300,
  drawerContainerClassName = "",
  lockBackgroundScroll = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}) => {
  const drawerStyles = useMemo(() => {
    const directionStyles = {
      left: {
        top: 0,
        left: 0,
        height: "100vh",
        width: size,
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: `transform ${duration}ms ease-in-out`,
      },
      right: {
        top: 0,
        right: 0,
        height: "100vh",
        width: size,
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: `transform ${duration}ms ease-in-out`,
      },
      top: {
        top: 0,
        left: 0,
        width: "100vw",
        height: size,
        transform: open ? "translateY(0)" : "translateY(-100%)",
        transition: `transform ${duration}ms ease-in-out`,
      },
      bottom: {
        bottom: 0,
        left: 0,
        width: "100vw",
        height: size,
        transform: open ? "translateY(0)" : "translateY(100%)",
        transition: `transform ${duration}ms ease-in-out`,
      },
    };

    return {
      ...directionStyles[direction],
    };
  }, [direction, size, open, duration]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && open && closeOnEscape) {
        onClose();
      }
    },
    [open, onClose, closeOnEscape]
  );

  useEffect(() => {
    if (lockBackgroundScroll && open) {
      const originalOverflow = document.body.style.overflow || "";
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open, lockBackgroundScroll]);

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Drawer"
      className={`fixed inset-0 z-[999999] transition-opacity ${
        open
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
      style={{
        transitionDuration: `${duration}ms`,
      }}
    >
      <div
        className="fixed inset-0 transition-opacity"
        style={{
          backgroundColor: overlayColor,
          opacity: overlayOpacity,
          transitionDuration: `${duration}ms`,
        }}
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        className={`${drawerContainerClassName} fixed z-50 shadow-popper-blur-effect backdrop-blur-[10px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk`}
        style={drawerStyles}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Drawer;
